import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Grid,
  LinearProgress,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAccount } from '../../contexts/Account';
import ACSnackbar from '../../components/ACSnackbar';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import { benefitsUpdate, updateCompanyOnboardingStatusCall } from '../../services/company_services';

const styles = () => ({
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
});

const benefitOptions = [
  { category: 'Potential Equity' },
  { category: 'Complimentary Products' },
  { category: 'Referral Bonus' },
  { category: 'Formal Roles' },
  { category: 'Executive Endorsement' },
];

function parseExistingBenefits(benefits = []) {
  return benefits
    .map((benefit) => {
      try {
        if (typeof benefit === 'string') {
          return JSON.parse(benefit);
        }
        return benefit;
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean)
    .map((benefit) => benefit.category)
    .filter(Boolean);
}

function CompanyOnboardingStep6() {
  const { accountDetails, asLoggedUserDetails, editAccountInformation } =
    useAccount();
  const history = useHistory();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = styles();

  const initialSelections = useMemo(
    () => new Set(parseExistingBenefits(accountDetails.benefits || [])),
    [accountDetails.benefits],
  );
  const [selectedCategories, setSelectedCategories] =
    useState(initialSelections);
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const disableButton = () => isLoading;

  const buildBenefitsPayload = () => {
    const selected = benefitOptions.filter((option) =>
      selectedCategories.has(option.category),
    );

    return selected.map((option, index) => ({
      category: option.category,
      details: '',
      featured: false,
      flareTag: null,
      order: index + 1,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const benefitsPayload = buildBenefitsPayload();

    try {
      setIsLoading(true);
      await Promise.all([
        benefitsUpdate({
          CONTACT_ID: asLoggedUserDetails.userId,
          COMPANY_BENEFITS: benefitsPayload.map((benefit) =>
            JSON.stringify(benefit),
          ),
        }),
        updateCompanyOnboardingStatusCall({
          COMPANY_ID: accountDetails.id,
          ONBOARDING_STATUS: 'Complete',
          IS_AOBF: true,
        }),
      ]);
      editAccountInformation({
        benefits: benefitsPayload,
      });
      history.push('/company/onboarding/step7');
    } catch (error) {
      console.log('Error updating benefits:', error);
      setIsLoading(false);
      setOpenSnackbar(true);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Setup
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Step 6/6
          </Typography>
        </Grid>
        <LinearProgress
          variant="determinate"
          value={(6 / 6) * 100}
          sx={{
            height: 8,
            borderRadius: 999,
            backgroundColor: '#E8EDF4',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              backgroundColor: '#2F6CFB',
            },
          }}
        />
      </Box>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          textAlign: 'center',
          letterSpacing: '-0.5px',
          color: '#000000',
          mb: 1,
        }}
      >
        How can you reward board members?
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          textAlign: 'center',
          letterSpacing: '-0.43px',
          color: '#646D7A',
          mb: 2,
        }}
      >
        You do not need to directly compensate board members, but consider
        providing perks for your advisors.
      </Typography>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '20px',
          textAlign: 'center',
          letterSpacing: '-0.3px',
          color: '#111827',
          mb: 2,
        }}
      >
        Select any that apply
      </Typography>

      <Grid>
        <Grid {...classes.gridItemProps} mb="16px">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {benefitOptions.map((option) => {
              const isSelected = selectedCategories.has(option.category);
              return (
                <ButtonBase
                  key={option.category}
                  onClick={() => toggleCategory(option.category)}
                  disableRipple
                  aria-pressed={isSelected}
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    borderRadius: '12px',
                    px: 2,
                    py: 1.5,
                    border: '1px solid #E0E7F1',
                    backgroundColor: isSelected ? '#E4E9F1' : '#F7F9FC',
                    transition: 'background-color 0.2s ease',
                    textAlign: 'left',
                    '&:hover': {
                      backgroundColor: isSelected ? '#DCE3EE' : '#E6EBF3',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '22px',
                      color: '#111827',
                      letterSpacing: '-0.3px',
                    }}
                  >
                    {option.category}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
        </Grid>
        <Grid
          {...classes.gridItemProps}
          display="flex"
          justifyContent="center"
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
              textAlign: 'center',
            }}
          >
            More perks available in your boardroom
          </Typography>
        </Grid>
        <Grid
          {...classes.gridItemProps}
          md={5}
          display="flex"
          justifyContent="center"
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={disableButton()}
            sx={{
              borderRadius: '15px',
              backgroundColor: '#3171F6',
              '&:hover': {
                backgroundColor: '#2A60E0',
              },
              height: '52px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 700,
              width: '100%',
              mb: isSM ? '24px' : null,
              my: '20px',
            }}
          >
            {isLoading ? <LoadingLogoSkeleton /> : 'Finish'}
          </Button>
        </Grid>
      </Grid>
      <ACSnackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        severity="error"
        text="Something went wrong"
      />
    </Box>
  );
}

export default CompanyOnboardingStep6;
